import { type ReactNode, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Factory, Layers, ListChecks, Plus, Search, Star, Users } from 'lucide-react';

import type { QuestionLibraryCatalogCourse } from 'services/api/adminContentClient';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import { assignmentCount, groupCoursesForBuilders } from '../courseGrouping';

export type CourseHomeView = 'all' | 'assigned' | 'other' | 'tests';
export type CourseHomeSort = 'suggested' | 'name' | 'recent' | 'learners';

const VIEWS: { value: CourseHomeView; label: string }[] = [
  { value: 'all', label: 'All courses' },
  { value: 'assigned', label: 'Assigned to learners' },
  { value: 'other', label: 'Not assigned' },
  { value: 'tests', label: 'Looks like a test or old copy' },
];

const SORTS: { value: CourseHomeSort; label: string }[] = [
  { value: 'suggested', label: 'Suggested' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'recent', label: 'Recently edited' },
  { value: 'learners', label: 'Most learners' },
];

export type CourseHomeProps = {
  courses: QuestionLibraryCatalogCourse[];
  loading: boolean;
  error: string;
  onRetry: () => void;
  onSelect: (courseId: number) => void;
  onCreate: () => void;
  createDisabled: boolean;
};

const plural = (count: number, word: string) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;

const byName = (a: QuestionLibraryCatalogCourse, b: QuestionLibraryCatalogCourse) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }) || a.id - b.id;

const sortCourses = (courses: QuestionLibraryCatalogCourse[], sort: CourseHomeSort) => {
  if (sort === 'suggested') return courses;
  const sorted = [...courses];
  if (sort === 'name') sorted.sort(byName);
  if (sort === 'recent') sorted.sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '') || byName(a, b));
  if (sort === 'learners') sorted.sort((a, b) => (b.learner_count ?? 0) - (a.learner_count ?? 0) || assignmentCount(b) - assignmentCount(a) || byName(a, b));
  return sorted;
};

const editedLabel = (updatedAt: string | undefined) => {
  if (!updatedAt) return null;
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return null;
  return `Edited ${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

/** "20 learners · 1 org · 2 cohorts", or null when nobody is assigned. */
const audienceLine = (course: QuestionLibraryCatalogCourse) => {
  const parts: string[] = [];
  if (course.learner_count) parts.push(plural(course.learner_count, 'learner'));
  if (course.organization_count) parts.push(plural(course.organization_count, 'org'));
  if (course.cohort_count) parts.push(plural(course.cohort_count, 'cohort'));
  return parts.length ? parts.join(' · ') : null;
};

function CourseCard({ course, onSelect }: { course: QuestionLibraryCatalogCourse; onSelect: (courseId: number) => void }) {
  const audience = audienceLine(course);
  const edited = editedLabel(course.updated_at);
  const hasBadges = course.is_life_plan || course.factory_managed || course.lifecycle_status === 'qa';
  return (
    <button
      type="button"
      onClick={() => onSelect(course.id)}
      data-testid={`course-list-item-${course.id}`}
      className="group flex h-full w-full flex-col gap-3 rounded-2xl border border-foundation-calm-blue-200/70 bg-white/70 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:bg-white hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {hasBadges ? <span className="flex flex-wrap gap-1.5">
        {course.is_life_plan ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            <Star className="h-3 w-3" aria-hidden="true" />Life Plan
          </span>
        ) : null}
        {course.factory_managed ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
            <Factory className="h-3 w-3" aria-hidden="true" />Course Factory
          </span>
        ) : null}
        {course.lifecycle_status === 'qa' ? (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">QA</span>
        ) : null}
      </span> : null}
      <span data-testid="course-card-name" className="block break-words text-base font-semibold leading-snug text-foreground">{course.name}</span>
      <span className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" aria-hidden="true" />{plural(course.module_count, 'module')}</span>
        {course.chapter_count ? (
          <span className="inline-flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" aria-hidden="true" />{plural(course.chapter_count, 'chapter')}</span>
        ) : null}
        {course.question_count ? (
          <span className="inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" aria-hidden="true" />{plural(course.question_count, 'question')}</span>
        ) : null}
      </span>
      <span className="mt-auto flex items-center gap-2 border-t border-foundation-calm-blue-200/50 pt-3 text-xs">
        <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className={cn('min-w-0 flex-1 truncate', audience ? 'font-medium text-foreground' : 'text-muted-foreground')}>
          {audience ?? 'Not assigned'}
        </span>
        {edited ? <span className="shrink-0 text-muted-foreground">{edited}</span> : null}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden="true" />
      </span>
    </button>
  );
}

const gridClass = 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3';

/**
 * Course Builder's landing page when no course is open: the whole active catalog as cards,
 * with the same grouping as the Module Builder course picker, filter chips, search and sort.
 */
export default function CourseHome({ courses, loading, error, onRetry, onSelect, onCreate, createDisabled }: CourseHomeProps) {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<CourseHomeView>('all');
  const [sort, setSort] = useState<CourseHomeSort>('suggested');
  const [showTests, setShowTests] = useState(false);
  const term = search.trim().toLocaleLowerCase();
  const searching = term.length > 0;

  const allGroups = useMemo(() => groupCoursesForBuilders(courses), [courses]);
  const groups = useMemo(() => {
    const filtered = groupCoursesForBuilders(searching ? courses.filter(course => course.name.toLocaleLowerCase().includes(term)) : courses);
    return {
      assigned: sortCourses(filtered.assigned, sort),
      other: sortCourses(filtered.other, sort),
      tests: sortCourses(filtered.tests, sort),
    };
  }, [courses, searching, term, sort]);

  const counts: Record<CourseHomeView, number> = {
    all: courses.length,
    assigned: allGroups.assigned.length,
    other: allGroups.other.length,
    tests: allGroups.tests.length,
  };
  const testsExpanded = view === 'tests' || showTests || searching;
  const shown = {
    assigned: view === 'all' || view === 'assigned' ? groups.assigned : [],
    other: view === 'all' || view === 'other' ? groups.other : [],
    tests: view === 'all' || view === 'tests' ? groups.tests : [],
  };
  const visibleCount = shown.assigned.length + shown.other.length + shown.tests.length;

  const section = (id: string, label: ReactNode, list: QuestionLibraryCatalogCourse[]) => (
    <section aria-labelledby={id} className="space-y-3">
      <h3 id={id} className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
        <span className="font-medium normal-case">· {list.length}</span>
      </h3>
      <div className={gridClass}>{list.map(course => <CourseCard key={course.id} course={course} onSelect={onSelect} />)}</div>
    </section>
  );

  return (
    <Card maxWidth="none" disableGlass disableAnimation className="min-w-0 px-2 sm:px-6" data-testid="course-builder-course-list-card">
      <CardContent variant="custom" className="space-y-5 py-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-1">
            <h2 className="text-2xl font-semibold text-foreground">Courses</h2>
            <p className="text-sm text-muted-foreground">Choose a course to edit its details, chapters and modules.</p>
          </div>
          <Button size="auto" onClick={onCreate} disabled={createDisabled} className="sm:w-auto">
            <Plus className="h-4 w-4" aria-hidden="true" />Create new course
          </Button>
        </div>

        <div className="flex flex-wrap gap-2" role="group" aria-label="Course views">
          {VIEWS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              aria-pressed={view === option.value}
              data-testid={`course-view-${option.value}`}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                view === option.value
                  ? 'border-foundation-base-darkbase-dark-500 bg-foundation-base-darkbase-dark-500 text-white'
                  : 'border-foundation-calm-blue-200 bg-white/70 text-foundation-base-darkbase-dark-500 hover:bg-white',
              )}
            >
              {option.label}
              {!loading ? <span className="ml-1.5 opacity-70">{counts[option.value].toLocaleString()}</span> : null}
            </button>
          ))}
        </div>

        <div className="grid gap-3 rounded-3xl bg-white/40 p-3 md:grid-cols-[minmax(0,1fr)_14rem]">
          <div className="space-y-1.5">
            <Label htmlFor="course-home-search" className="text-xs uppercase text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id="course-home-search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search courses by name"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="course-home-sort" className="text-xs uppercase text-muted-foreground">Sort</Label>
            <Select value={sort} onValueChange={value => setSort(value as CourseHomeSort)}>
              <SelectTrigger id="course-home-sort" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORTS.map(option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}{' '}
            <Button size="auto" variant="ghost" onClick={onRetry}>Retry course list</Button>
          </p>
        )}

        {loading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <LoadingAnimation />
          </div>
        ) : (
          <div className="space-y-6" data-testid="course-builder-course-list">
            {shown.assigned.length > 0 && section('course-home-assigned', <><Users className="h-3.5 w-3.5" aria-hidden="true" />Assigned to learners</>, shown.assigned)}
            {shown.other.length > 0 && section('course-home-other', view === 'other' ? 'Not assigned' : 'Other courses', shown.other)}
            {shown.tests.length > 0 && (
              <section className="space-y-3" aria-label="Looks like a test or old copy">
                {view === 'tests' ? (
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Looks like a test or old copy <span className="font-medium normal-case">· {shown.tests.length}</span>
                  </h3>
                ) : (
                  <button
                    type="button"
                    aria-expanded={testsExpanded}
                    aria-controls="course-home-tests"
                    onClick={() => setShowTests(value => !value)}
                    className="flex w-full items-center gap-1.5 rounded-lg text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {testsExpanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
                    Looks like a test or old copy
                    <span className="font-medium normal-case">· {shown.tests.length}</span>
                    {!testsExpanded ? <span className="ml-2 font-medium normal-case">Show</span> : null}
                  </button>
                )}
                <div id="course-home-tests" hidden={!testsExpanded} className={gridClass}>
                  {testsExpanded ? shown.tests.map(course => <CourseCard key={course.id} course={course} onSelect={onSelect} />) : null}
                </div>
              </section>
            )}
            {!visibleCount && (
              <p className="rounded-2xl border border-dashed border-foundation-calm-blue-200 py-10 text-center text-sm text-muted-foreground">
                {searching ? 'No courses match your search.' : 'No courses found.'}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
